import * as React from 'react';

import { Textarea as ShadcnTextarea } from '@/hen-ui/shadcn/textarea';
import { Flex } from '@/hen-ui/composites/Flex';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import {
  textareaVariants,
  type TextareaVariantProps,
} from './textarea.variants';
import { cn } from '@/hen-ui/utils';
import {
  fieldPlaceholderColorClass,
  fieldPlaceholderSizeClass,
  fieldTextColorClass,
  fieldTextSizeClass,
  type FieldTextStyleProps,
} from '@/hen-ui/utils/field-text-style';

const REMAINING_THRESHOLD_RATIOS = [0.5, 0.25, 0.1] as const;

/** Match native textarea length: CRLF / lone CR count as a single LF. */
const measureTextLength = (text: string) => text.replace(/\r\n?/g, '\n').length;

const getRemainingThresholds = (maxLength: number): number[] => {
  const ratioThresholds = REMAINING_THRESHOLD_RATIOS.map((ratio) =>
    Math.floor(maxLength * ratio),
  );
  return [...ratioThresholds, 0].filter(
    (threshold, index, all) =>
      threshold >= 0 &&
      threshold <= maxLength &&
      all.indexOf(threshold) === index,
  );
};

const formatRemaining = (remaining: number) =>
  `${remaining} characters remaining.`;

/** A background utility, including gradient stops and state prefixes. */
const BACKGROUND_UTILITY = /(?:^|:)(?:!)?(?:bg-|from-|via-|to-)/;

/**
 * Background classes from the caller, moved onto the field layer.
 * A plain div is never `:disabled` or `:hover` the way the textarea is,
 * so those prefixes follow the textarea instead.
 *
 * @param className - The className passed to the textarea
 * @returns The background utilities, or an empty string
 */
const fieldBackgroundClassName = (className?: string): string => {
  if (!className) return '';

  return className
    .split(/\s+/)
    .filter((token) => BACKGROUND_UTILITY.test(token))
    .map((token) =>
      token
        .replace(/^disabled:/, 'has-[:disabled]:')
        .replace(/^hover:/, 'has-[:hover]:'),
    )
    .join(' ');
};

const BACKGROUND_STYLE_KEYS = [
  'background',
  'backgroundColor',
  'backgroundImage',
  'backgroundPosition',
  'backgroundRepeat',
  'backgroundSize',
  'backgroundAttachment',
  'backgroundClip',
  'backgroundOrigin',
] as const satisfies readonly (keyof React.CSSProperties)[];

/**
 * Splits a style so the field layer owns the background and the textarea
 * keeps everything else.
 *
 * @param style - The style passed to the textarea
 * @returns The background styles and the remaining styles
 */
const splitBackgroundStyle = (
  style?: React.CSSProperties,
): {
  field?: React.CSSProperties;
  rest?: React.CSSProperties;
} => {
  if (!style) return {};

  const field: React.CSSProperties = {};
  const rest: React.CSSProperties = {};
  let hasField = false;
  let hasRest = false;

  for (const key of Object.keys(style) as (keyof React.CSSProperties)[]) {
    if (
      (
        BACKGROUND_STYLE_KEYS as readonly (keyof React.CSSProperties)[]
      ).includes(key)
    ) {
      field[key] = style[key] as never;
      hasField = true;
    } else {
      rest[key] = style[key] as never;
      hasRest = true;
    }
  }

  return {
    field: hasField ? field : undefined,
    rest: hasRest ? rest : undefined,
  };
};

const useCharacterCountAnnouncement = (
  maxLength: number | undefined,
  remaining: number,
) => {
  const thresholds = React.useMemo(
    () => (maxLength === undefined ? [] : getRemainingThresholds(maxLength)),
    [maxLength],
  );
  const band =
    maxLength === undefined
      ? 0
      : thresholds.filter((threshold) => remaining <= threshold).length;
  const bandRef = React.useRef(band);
  const maxLengthRef = React.useRef(maxLength);
  const [announcement, setAnnouncement] = React.useState('');

  React.useEffect(() => {
    // A new limit (or the counter turning on) re-baselines silently.
    if (maxLength !== maxLengthRef.current) {
      setAnnouncement('');
    } else if (band > bandRef.current) {
      setAnnouncement(formatRemaining(remaining));
    } else if (band < bandRef.current) {
      setAnnouncement('');
    }
    maxLengthRef.current = maxLength;
    bandRef.current = band;
  }, [band, maxLength, remaining]);

  return announcement;
};

export interface TextareaProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnTextarea>,
    TextareaVariantProps,
    FieldTextStyleProps {
  /**
   * Error state - when true, displays error styling and sets aria-invalid
   */
  error?: boolean;
  /**
   * Error message text - when provided, sets aria-describedby and aria-invalid
   */
  errorMessage?: string;
  /**
   * Helper text below the textarea. Omitted when `errorMessage` is set (error takes precedence).
   * Also omitted when `showCharacterCount` is active with a valid `maxLength` (counter occupies this slot).
   */
  helperText?: string;
  /**
   * Field name shown above the answer when variant is 'two-line'.
   */
  label?: string;
  /**
   * When true and `maxLength` is set, shows a live "characters remaining" count in the helper slot.
   */
  showCharacterCount?: boolean;
}

/**
 * Textarea component following HEN Design System standards.
 *
 * A standardized multi-line text input wrapping the native HTML textarea element
 * with HEN-specific variants and design tokens.
 */
export const Textarea = React.forwardRef<
  React.ElementRef<typeof ShadcnTextarea>,
  TextareaProps
>(
  (
    {
      className,
      variant,
      size,
      error,
      errorMessage,
      helperText,
      label,
      resize,
      showCharacterCount,
      maxLength,
      placeholderSize,
      valueSize,
      placeholderColor,
      labelColor,
      id,
      value,
      defaultValue,
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      onFocus,
      onBlur,
      onChange,
      ...props
    },
    ref,
  ) => {
    const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
    const textareaId = React.useId();
    const finalId = id || textareaId;
    const errorId = errorMessage ? `${finalId}-error` : undefined;
    // NaN, Infinity, and negative limits never reach the DOM.
    const characterLimit =
      typeof maxLength === 'number' &&
      Number.isFinite(maxLength) &&
      maxLength >= 0
        ? maxLength
        : undefined;
    const counterMax =
      showCharacterCount && !errorMessage ? characterLimit : undefined;
    const showCounter = counterMax !== undefined;
    const helperId =
      !errorMessage && (showCounter || helperText)
        ? `${finalId}-helper`
        : undefined;
    const labelId = label ? `${finalId}-label` : undefined;

    const [isFocused, setIsFocused] = React.useState(false);
    const [uncontrolledLength, setUncontrolledLength] = React.useState(() =>
      measureTextLength(String(defaultValue ?? '')),
    );
    const isControlled = value !== undefined;

    React.useEffect(() => {
      if (!isControlled && textareaRef.current) {
        setUncontrolledLength(measureTextLength(textareaRef.current.value));
      }
    }, [defaultValue, isControlled]);

    React.useEffect(() => {
      const textarea = textareaRef.current;
      const form = textarea?.form;

      if (isControlled || !textarea || !form) {
        return;
      }

      const handleReset = () => {
        queueMicrotask(() =>
          setUncontrolledLength(measureTextLength(textarea.value)),
        );
      };

      form.addEventListener('reset', handleReset);
      return () => form.removeEventListener('reset', handleReset);
    }, [isControlled]);

    const currentLength = isControlled
      ? measureTextLength(String(value ?? ''))
      : uncontrolledLength;
    const hasCurrentValue = isControlled
      ? Boolean(value)
      : uncontrolledLength > 0;
    const remaining =
      counterMax === undefined ? 0 : Math.max(0, counterMax - currentLength);
    const announcement = useCharacterCountAnnouncement(counterMax, remaining);
    const isFloating = variant === 'two-line' && (isFocused || hasCurrentValue);

    const describedBy =
      [ariaDescribedBy, helperId, errorId].filter(Boolean).join(' ') ||
      undefined;

    const internalLabelledBy =
      variant === 'two-line' && label && !ariaLabel ? labelId : undefined;
    const labelledBy =
      [internalLabelledBy, ariaLabelledBy].filter(Boolean).join(' ') ||
      undefined;

    const isInvalid = error || Boolean(errorMessage);
    const labeledTwoLine = variant === 'two-line' && Boolean(label);
    const finalAriaInvalid =
      ariaInvalid !== undefined ? ariaInvalid : isInvalid ? 'true' : undefined;

    const handleFocus = (event: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(true);
      onFocus?.(event);
    };

    const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(false);
      onBlur?.(event);
    };

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (!isControlled) {
        setUncontrolledLength(measureTextLength(event.target.value));
      }
      onChange?.(event);
    };

    const setTextareaRef = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        textareaRef.current = node;

        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current =
            node;
        }
      },
      [ref],
    );

    // Overlay scrollbars take no space. Classic ones do, and the width is not
    // the same in every browser. The cover stops where the scrollbar starts.
    const [scrollbarGutter, setScrollbarGutter] = React.useState<number | null>(
      null,
    );
    const measureScrollbar = React.useCallback(() => {
      const node = textareaRef.current;
      if (!node) {
        return;
      }
      const styles = getComputedStyle(node);
      const borders =
        Number.parseFloat(styles.borderLeftWidth) +
        Number.parseFloat(styles.borderRightWidth);
      const gutter = node.offsetWidth - node.clientWidth - borders;
      if (!Number.isFinite(gutter)) {
        return;
      }
      setScrollbarGutter(Math.max(0, Math.round(gutter)));
    }, []);

    React.useLayoutEffect(() => {
      if (!labeledTwoLine) {
        return;
      }
      measureScrollbar();
      const node = textareaRef.current;
      if (!node || typeof ResizeObserver === 'undefined') {
        return;
      }
      const observer = new ResizeObserver(() => {
        measureScrollbar();
      });
      // The scrollbar changes the content box, not the border box.
      observer.observe(node, { box: 'content-box' });
      return () => observer.disconnect();
    }, [labeledTwoLine, measureScrollbar, currentLength]);

    const { style: callerStyle, ...textareaProps } = props;
    const { field: fieldBackgroundStyle, rest: textareaStyle } =
      splitBackgroundStyle(callerStyle);

    const textareaControl = (
      <ShadcnTextarea
        {...textareaProps}
        style={labeledTwoLine ? textareaStyle : callerStyle}
        className={cn(
          textareaVariants({ variant, size, error: isInvalid, resize }),
          valueSize && fieldTextSizeClass[valueSize],
          // ::placeholder inherits the field's size, so pin it once valueSize moves that.
          (placeholderSize || valueSize) &&
            fieldPlaceholderSizeClass[
              placeholderSize ?? (size === 'lg' ? 'b4' : 'b5')
            ],
          placeholderColor && fieldPlaceholderColorClass[placeholderColor],
          className,
          // The field layer owns the fill. A background left on the textarea
          // would hide that layer and leave the name cover on the token color.
          // Resize lives on the shell so the fill stays with the dragged edge,
          // and the shell draws the focus ring so the shell's overflow does
          // not clip it.
          labeledTwoLine &&
            'h-full resize-none bg-transparent disabled:bg-transparent disabled:opacity-100 focus-visible:ring-0',
        )}
        ref={setTextareaRef}
        id={finalId}
        maxLength={characterLimit}
        value={value}
        defaultValue={defaultValue}
        aria-invalid={finalAriaInvalid}
        aria-describedby={describedBy}
        aria-label={ariaLabel}
        aria-labelledby={labelledBy}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        data-hen-id={COMPONENT_IDS.Textarea}
      />
    );

    const fieldResizeClass =
      resize === 'none'
        ? 'resize-none'
        : resize === 'both'
          ? 'resize'
          : 'resize-y';
    const fieldLayerStyle = {
      ...fieldBackgroundStyle,
      ...(scrollbarGutter == null
        ? {}
        : { '--textarea-scrollbar-width': `${scrollbarGutter}px` }),
    } as React.CSSProperties;

    return (
      <Flex direction='column' gap='min' width='full'>
        {labeledTwoLine ? (
          <div
            className={cn(
              'relative grid w-full overflow-hidden rounded-field',
              size === 'sm'
                ? 'min-h-[64px]'
                : size === 'lg'
                  ? 'min-h-[120px]'
                  : 'min-h-[88px]',
              fieldResizeClass,
              isInvalid
                ? 'focus-within:ring-2 focus-within:ring-error-100 focus-within:ring-offset-0'
                : 'focus-within:ring-field-focus focus-within:ring-field-focus-width focus-within:ring-offset-0',
            )}
          >
            <div
              className={cn(
                'relative col-start-1 row-start-1 h-full min-h-0 w-full rounded-field bg-surface-field',
                'has-[:disabled]:bg-neutral-100 has-[:disabled]:opacity-60',
                fieldBackgroundClassName(className),
                // Hides scrolled lines in the name's padding, plus the empty
                // leading above the first line. The cover inherits this
                // layer's fill, so a caller background stays in sync. The top
                // is 0.75px, just inside the 1px border, so Brave does not
                // show a sliver of scrolled text above it. The right inset
                // is the border plus the measured scrollbar.
                'before:pointer-events-none before:absolute before:z-10 before:block before:content-[""]',
                'before:top-[0.75px] before:left-px before:right-[calc(var(--textarea-scrollbar-width,1rem)+1px)]',
                'before:h-[calc(var(--space-lg,24px)+4px)]',
                'before:rounded-tl-[calc(var(--radius-field,var(--radius-sm,8px))-1px)]',
                'before:[background:inherit]',
              )}
              style={fieldLayerStyle}
            >
              {textareaControl}
            </div>
            <label
              id={labelId}
              htmlFor={finalId}
              className={cn(
                'col-start-1 row-start-1 absolute z-20 pointer-events-none',
                size === 'lg' ? 'left-sm' : 'left-xs',
                'transition-all duration-200 ease-in-out',
                'text-body font-body',
                isFloating
                  ? 'top-xxs text-l2'
                  : cn(
                      size === 'sm' ? 'top-xxs' : 'top-xs',
                      placeholderSize
                        ? fieldTextSizeClass[placeholderSize]
                        : 'text-b5',
                    ),
                isInvalid && isFloating
                  ? 'text-error-500'
                  : isFloating
                    ? labelColor
                      ? fieldTextColorClass[labelColor]
                      : 'text-body'
                    : placeholderColor
                      ? fieldTextColorClass[placeholderColor]
                      : 'text-subtext',
              )}
            >
              {label}
            </label>
          </div>
        ) : (
          textareaControl
        )}
        {helperId && (
          <div
            id={helperId}
            className='text-caption leading-caption text-subtext'
          >
            {showCounter ? formatRemaining(remaining) : helperText}
          </div>
        )}
        {showCounter && (
          <div role='status' aria-live='polite' className='sr-only'>
            {announcement}
          </div>
        )}
        {errorMessage && (
          <div
            id={errorId}
            className='text-sm text-error-500'
            role='alert'
            aria-live='polite'
          >
            {errorMessage}
          </div>
        )}
      </Flex>
    );
  },
);

Textarea.displayName = 'Textarea';
